import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { withRouter } from 'react-router';
import { createStructuredSelector } from 'reselect';
import cx from 'classnames';
import { UPLOAD_SOURCES } from '@cld/upload-widget-common';
import t from 'common/i18n';
import appConfig from 'appConfig';
import { asAbsolute } from 'root/routes/utils';
import TabPanel from 'tabs/hocs/TabPanel';
import FiltersPanel from 'tabs/ShutterStock/components/FiltersPanel';
import RouteWithSubRoutes from 'root/components/RouteWithSubRoutes';
import ServiceLoginIntro from 'root/components/ServiceLoginIntro';
import WithSidePanel from 'root/components/WithSidePanel';
import { selectCloudName, selectIsMultipleEnabled, selectNewTlsDomain } from 'root/selectors';
import getShutterStock from './service';
import consts from './data/consts';
import bindActions, { types as ats } from './actions';
import {
  selectFiltersPanelOpen,
  selectHasSearchParams,
  selectHasSearchTerm,
  selectIsLoggedIn,
  selectIsListeningForAuth,
  selectContextItems,
  selectShutterstockServer,
  selectPurchaseSuccessResult,
  selectIsPurchaseInProgress,
} from './selectors';
import Header from './components/Header';
import Footer from './components/Footer';
import SelectionPage from './components/SelectionPage';
import SVG_ICONS from '~/assets/icons/icons';

import './ShutterStockTab.scss';

const { SHUTTERSTOCK } = UPLOAD_SOURCES;

export const listenForAuth = (props) => {
  let result;

  if (!props.isLoggedIn && !props.listeningForAuth && (!props.route || !~props.route.location.pathname.indexOf('auth'))) {
    const setAuthInfoAction = props.actions[ats.SET_AUTH_INFO];
    const { cloudName } = props;

    props.actions[ats.INIT_AUTH](cloudName);

    result = getShutterStock()
      .setAuthListener(cloudName)
      .then((result) => setAuthInfoAction({ cloudName, token: result.token }));
  }

  return result || Promise.resolve(); // returning for test purposes
};

export class ShutterstockTab extends Component {
  componentDidMount() {
    listenForAuth(this.props);
  }

  componentDidUpdate(prevProps) {
    this.handleInitialization(prevProps);

    if (!this.props.hasSearchParams && prevProps.hasSearchParams) {
      this.navigateInternally();
    } else if ((this.props.hasSearchTerm || this.props.hasSearchParams) && this.getIsInRoute(SHUTTERSTOCK, true)) {
      this.doSearch();
    }

    if (prevProps.isPurchaseInProgress && !this.props.isPurchaseInProgress) {
      this.clearSelection(); // only one context so its fine. if there are more then should add clear-all action !
    }

    this.handlePurchaseSuccess();
  }

  handleInitialization = (prevProps) => {
    const { isLoggedIn } = this.props;
    const { isLoggedIn: prevIsLoggedIn } = prevProps;

    if (!isLoggedIn && isLoggedIn !== prevIsLoggedIn) {
      listenForAuth(this.props);
    } else if (isLoggedIn && !prevIsLoggedIn) {
      this.props.actions[ats.GET_CATEGORIES]();
      this.initializeImages();
    }
  };

  handlePurchaseSuccess = () => {
    const purchasedUrl = this.props.purchaseSuccessResult && this.props.purchaseSuccessResult.url;

    if (purchasedUrl) {
      this.props.history.goBack(); // go back from purchase page

      this.props.addToUploadQueue({
        files: [purchasedUrl],
      });

      this.props.actions[ats.SET_PURCHASE_SUCCESS](null); // clear purchase success result
    }
  };

  navigateInternally = (page) => {
    const path = page ? asAbsolute(SHUTTERSTOCK, page) : asAbsolute(SHUTTERSTOCK);

    this.props.history.push(path);
  };

  navigateToPurchase = () => {
    this.props.actions[ats.SET_PURCHASE_START]();
    this.navigateInternally(consts.ROUTES.PURCHASE);

    if (this.props.isFilterPanelOpen) {
      this.props.actions[ats.TOGGLE_FILTERS_PANEL]();
    }
  };

  onNoAuthActionClick = () => {
    getShutterStock().openAuthWindow({
      cloudName: this.props.cloudName,
      server: this.props.shutterstockServer,
      newTlsDomain: this.props.newTlsDomain,
    });
  };

  onLogout = () => {
    this.navigateInternally();
    this.props.actions[ats.DO_LOGOUT]();
  };

  onHeaderBackNavigation = () => this.props.actions[ats.CLEAR_PURCHASE_DETAILS]();

  clearSelection = () => {
    this.props.actions[ats.CLEAR_SELECTION_FOR_CONTEXT]({
      context: consts.CONTEXTS.SEARCH, // only have 1 context
    });
  };

  getIsInRoute = (route, exact = false) =>
    exact ? this.props.route.location.pathname === asAbsolute(route) : !!~this.props.route.location.pathname.indexOf(route);

  getIsInPurchaseRoute = () => this.getIsInRoute(consts.ROUTES.PURCHASE);

  getIsInSearchRoute = () => this.getIsInRoute(consts.ROUTES.SEARCH);

  initializeImages = () => {
    this.props.actions[ats.RETRIEVE_ASSETS](null);
  };

  purchase = (isLicenced) => {
    if (isLicenced) {
      this.props.actions[ats.DOWNLOAD]();
    } else {
      this.props.actions[ats.PURCHASE]();
    }
  };

  doSearch = (params) => {
    if (!this.getIsInSearchRoute()) {
      // navigate to search route if not there already
      this.navigateInternally(consts.ROUTES.SEARCH);
    }

    this.props.actions[ats.SET_SEARCH_PARAMS]({
      ...params,
      maxResults: consts.MAX_RESULTS.SEARCH,
    });
  };

  clearSearchTerm = () => {
    this.props.actions[ats.CLEAR_SEARCH_TERM]();
  };

  clearSearchFilters = () => {
    this.props.actions[ats.CLEAR_SEARCH_FILTERS]();
  };

  onItemSelected = (id) => {
    this.props.actions[ats.SET_SELECTION_FOR_CONTEXT]({
      context: consts.CONTEXTS.SEARCH, // only have 1 context
      id,
    });
  };

  renderSubRoutes() {
    return this.props.routes.map((r) => (
      <RouteWithSubRoutes key={`st_route_${r.path}`} {...r} doSearch={this.doSearch} onSelect={this.onItemSelected} isMultiple={this.props.isMultiple} />
    ));
  }

  renderSidePanel = () => <FiltersPanel doSearch={this.doSearch} />;

  sidePanel = !this.getIsInPurchaseRoute() ? this.renderSidePanel : null;

  renderAuthedContent = () => (
    <div className={cx('h-100 w-100')}>
      <WithSidePanel sidePanel={this.sidePanel} openSidePanel={this.props.isFilterPanelOpen}>
        <div className="tab-authed flex flex-column">
          <Header
            onLogout={this.onLogout}
            onBackNavigation={this.onHeaderBackNavigation}
            showSearch={!this.getIsInPurchaseRoute()}
            doSearch={this.doSearch}
            clearSearchTerm={this.clearSearchTerm}
            clearSearchFilters={this.clearSearchFilters}
            hasSearchTerm={this.props.hasSearchTerm}
            isInPurchase={this.getIsInPurchaseRoute()}
          />
          <Switch>
            {this.renderSubRoutes()}
            <Route render={() => <SelectionPage onSelect={this.onItemSelected} doSearch={this.doSearch} isMultiple={this.props.isMultiple} />} />
          </Switch>
        </div>
      </WithSidePanel>
    </div>
  );

  getIntroFooter = () => (
    <a className="c-link" href={appConfig.shutterStock.registrationLink} target="_blank" rel="noopener noreferrer">
      {t('shutterstock.reg_link_text')}
    </a>
  );

  renderTabContent = () => {
    let content;

    if (this.props.isLoggedIn) {
      content = this.renderAuthedContent();
    } else {
      content = (
        <ServiceLoginIntro
          key="shutter_login_intro"
          title={t('shutterstock.no_auth_title')}
          actionTitle={t('shutterstock.no_auth_action')}
          dataTest="connect-to-shutter-stock-btn"
          btnIcon={SVG_ICONS.SHUTTERSTOCK}
          btnClass="btn-shutter"
          statement={t('shutterstock.statement')}
          actionHandler={this.onNoAuthActionClick}
          footer={this.getIntroFooter()}
        />
      );
    }

    return content;
  };

  render() {
    return (
      <div
        className={cx('content-wrap bg-source-bg bg-source-bg-c shutterstock-block', {
          'user-active': this.props.isLoggedIn,
        })}
      >
        <div className="holder large-thin-menu-holder">{this.renderTabContent()}</div>

        <Footer
          purchase={this.purchase}
          isInPurchase={this.getIsInPurchaseRoute()}
          navigateToPurchase={this.navigateToPurchase}
          clearSelection={this.clearSelection}
        />
      </div>
    );
  }
}

export default TabPanel({
  id: SHUTTERSTOCK,
})(
  connect(
    createStructuredSelector({
      cloudName: selectCloudName,
      isLoggedIn: selectIsLoggedIn,
      isMultiple: selectIsMultipleEnabled,
      listeningForAuth: selectIsListeningForAuth,
      contextData: selectContextItems,
      hasSearchParams: selectHasSearchParams,
      hasSearchTerm: selectHasSearchTerm,
      shutterstockServer: selectShutterstockServer,
      purchaseSuccessResult: selectPurchaseSuccessResult,
      isPurchaseInProgress: selectIsPurchaseInProgress,
      isFilterPanelOpen: selectFiltersPanelOpen,
      newTlsDomain: selectNewTlsDomain,
    }),
    (dispatch) => ({
      actions: bindActions(dispatch),
    })
  )(withRouter(ShutterstockTab))
);
